import React, { useId, useState } from 'react'
import { ReactComponent as InfoIcon } from '@assets/images/Icons/Info_circle.svg'
import { ReactComponent as ArrowDownIcon } from '@assets/images/Icons/Arrow_down_chevron.svg'
import type { AccordionItemProps } from './Accordion.types'
import { useAccordionContext } from './AccordionContext'
import './Accordion.css'

const AccordionItem: React.FC<AccordionItemProps> = ({
  id,
  title,
  leading,
  children,
  showInfo = false,
  onInfoClick,
  expanded,
  defaultExpanded = false,
  onToggle,
  disabled = false,
  darkMode = false,
  className = '',
  style,
}) => {
  const generatedId = useId()
  const itemId = id ?? generatedId
  const accordion = useAccordionContext()

  const [localExpanded, setLocalExpanded] = useState(defaultExpanded)
  const isControlled = expanded !== undefined
  const isOpen = isControlled
    ? expanded
    : accordion
      ? accordion.isExpanded(itemId)
      : localExpanded
  const isDark = darkMode || Boolean(accordion?.darkMode)

  const handleToggle = (): void => {
    if (disabled) {
      return
    }

    const nextExpanded = !isOpen

    if (isControlled) {
      onToggle?.(nextExpanded)
      return
    }

    if (accordion) {
      accordion.toggleItem(itemId)
    } else {
      setLocalExpanded(nextExpanded)
    }

    onToggle?.(nextExpanded)
  }

  const handleHeaderKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ): void => {
    if (disabled) {
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleToggle()
    }
  }

  const handleInfoClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.stopPropagation()
    onInfoClick?.()
  }

  const contentId = `${itemId}-content`
  const isGrouped = accordion?.grouped ?? false

  return (
    <div
      className={`ds-accordion-item${
        isOpen ? ' ds-accordion-item--expanded' : ''
      }${isGrouped ? ' ds-accordion-item--grouped' : ''}${
        isDark ? ' ds-accordion-item--dark' : ''
      } ${className}`.trim()}
      style={style}
    >
      <div
        className="ds-accordion-item__header"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        aria-controls={contentId}
        aria-disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleHeaderKeyDown}
      >
        <span className="ds-accordion-item__leading">
          {leading != null && (
            // The slot can hold interactive controls, so keep their events away
            // from the header, which toggles the item on click / Enter / Space.
            <span
              className="ds-accordion-item__start"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              {leading}
            </span>
          )}
          <span className="ds-accordion-item__title">{title}</span>
          {showInfo && (
            <button
              type="button"
              className="ds-accordion-item__info"
              aria-label={`More information about ${title}`}
              onClick={handleInfoClick}
            >
              <InfoIcon aria-hidden="true" />
            </button>
          )}
        </span>
        <span className="ds-accordion-item__chevron" aria-hidden="true">
          <ArrowDownIcon
            className={`ds-accordion-item__chevron-icon${
              isOpen ? '' : ' ds-accordion-item__chevron-icon--collapsed'
            }`}
          />
        </span>
      </div>

      {isOpen && children != null && (
        <div
          id={contentId}
          className="ds-accordion-item__content"
          role="region"
        >
          {children}
        </div>
      )}
    </div>
  )
}

export default AccordionItem
